import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

export default function Login() {
    const { user, login, error } = useAuth();
    const [email, setEmail] = useState("admin@fundle.ai");
    const [password, setPassword] = useState("Fundle@2026");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    if (user && user !== false) return <Navigate to="/" replace />;

    async function submit(e) {
        e.preventDefault();
        setLoading(true);
        const ok = await login(email, password);
        setLoading(false);
        if (ok) navigate("/", { replace: true });
    }

    return (
        <div className="relative flex min-h-screen w-full bg-background text-foreground">
            {/* Left brand pane */}
            <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[hsl(var(--sidebar))] p-10 text-white lg:flex">
                <div className="grid-bg absolute inset-0 opacity-40" />
                <div className="relative z-10 flex items-center gap-2.5">
                    <img
                        src="/brand/fundle_logo_white.png"
                        alt="Fundle"
                        className="h-8 w-auto"
                        data-testid="login-brand-logo"
                    />
                    <div className="ml-1 leading-tight">
                        <div className="text-[10px] uppercase tracking-widest text-white/60">
                            MBO Operating System
                        </div>
                    </div>
                </div>

                <div className="relative z-10 max-w-md space-y-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">
                        The AI Operating System for Multi-Brand Retail Distribution
                    </div>
                    <h1 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                        Govern the network.
                        <br />
                        Command the numbers.
                    </h1>
                    <p className="max-w-sm text-[13px] leading-relaxed text-white/70">
                        One operating layer for sell-in, sell-out, inventory, schemes, targets, retail execution and
                        AI-assisted decisions — across every brand, distributor, and MBO.
                    </p>

                    <div className="mt-8 grid grid-cols-3 gap-3 border-t border-white/10 pt-6">
                        <StatMini label="Stores" value="30+" />
                        <StatMini label="SKUs" value="45+" />
                        <StatMini label="Regions" value="4" />
                    </div>
                </div>

                <div className="relative z-10 text-[11px] text-white/40">
                    &copy; 2026 Fundle. All rights reserved.
                </div>
            </div>

            {/* Right form pane */}
            <div className="relative flex w-full items-center justify-center p-6 lg:w-1/2 lg:p-12">
                <form
                    onSubmit={submit}
                    data-testid="login-form"
                    className="w-full max-w-sm space-y-5"
                >
                    <div className="space-y-1">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                            Sign in
                        </div>
                        <h2 className="text-2xl font-semibold tracking-tight">Welcome back</h2>
                        <p className="text-[13px] text-muted-foreground">
                            Access the Fundle command center.
                        </p>
                    </div>

                    <label className="block space-y-1.5">
                        <span className="mlabel">Work email</span>
                        <input
                            type="email"
                            data-testid="login-email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full rounded-sm border border-border bg-background px-3 py-2 text-[14px] outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/40"
                        />
                    </label>

                    <label className="block space-y-1.5">
                        <span className="mlabel">Password</span>
                        <input
                            type="password"
                            data-testid="login-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full rounded-sm border border-border bg-background px-3 py-2 text-[14px] outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/40"
                        />
                    </label>

                    {error && (
                        <div
                            data-testid="login-error"
                            className="rounded-sm border border-destructive/30 bg-destructive/[0.06] px-3 py-2 text-[12px] text-destructive"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="login-submit-btn"
                        className="flex w-full items-center justify-center gap-2 rounded-sm bg-primary px-4 py-2.5 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
                    >
                        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Sign in
                    </button>

                    <div className="rounded-sm border border-border bg-muted/30 p-3 text-[11px] leading-relaxed text-muted-foreground">
                        <div className="mlabel mb-1">Demo credentials</div>
                        <div className="mono-num">admin@fundle.ai / Fundle@2026</div>
                        <div className="mono-num">priya.exec@fundle.ai / Demo@2026</div>
                    </div>
                </form>
            </div>
        </div>
    );
}

function StatMini({ label, value }) {
    return (
        <div>
            <div className="mono-num text-2xl font-semibold text-white">{value}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
        </div>
    );
}
